import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { EcEnv } from '@castlery/config';
import { CustomerAddressEntity, CustomerAddressEntity_V2 } from '@castlery/types';

export const PhoneFormatRegexMapping = {
  SG: /^(\+?65(\s|-)?)?\d{4}(\s|-)?\d{4}$/,
  AU: /^(?:\+?61|0)[ -]?[2-478](?:[ -]?\d){8}$/,
  US: /^(\+1\s?)?(\(?[23456789]{1}[0-9]{2}\)?)[\s-]?\d{3}[\s-]?\d{4}$/,
  CA: /^(\+1\s?)?(\(?[23456789]{1}[0-9]{2}\)?)[\s-]?\d{3}[\s-]?\d{4}$/,
  UK: /^(?:\+44[\s-]?|0)(?:\d[\s-]?){9,10}$/,
};

export const formItemLayout = {
  oneColumn: {
    gridColumn: { xs: 'span 12', lg: 'span 12' },
  },
  twoColumn: {
    gridColumn: { xs: 'span 12', lg: 'span 6' },
  },
};

export interface AddressFormField {
  key: string;
  translationKey: string;
  type: 'input' | 'select' | 'checkbox';
  label?: string;
  placeholder?: string;
  value?: string;
  required: boolean;
  options?: {
    value: string;
    label: string;
  }[];
  validation?:
    | {
        minLength?: number;
        maxLength?: number;
      }
    | {
        validate: (value: string) => boolean | string;
      }
    | {
        pattern: RegExp;
      };
  formatter?: (value: string) => string;
  layoutStyle: {
    gridColumn: {
      xs: string;
      lg: string;
    };
  };
  disabled?: boolean;
  defaultValueReWrite?: (defaultAddress: CustomerAddressEntity | CustomerAddressEntity_V2) => string;
}

export const validatePostcode = (postcode: string) => {
  let isValid = false;
  const countryCode = EcEnv.NEXT_PUBLIC_COUNTRY.toUpperCase();
  if (postcodeValidatorExistsForCountry(countryCode)) {
    isValid = postcodeValidator(postcode, countryCode);
  } else {
    isValid = true;
  }
  return isValid;
};

export const zipcodeFormattingUtils = {
  SG: (value: string) => value,
  AU: (value: string) => value,
  US: (value: string) => value,
  CA: (value: string) => {
    if (!value) return value;
    const upperCaseValue = value.toUpperCase();
    const str = upperCaseValue.replace(/\s/g, '');
    const match = str.match(/^(\w{3})(\w{3})$/);
    if (match) {
      const str = `${match[1]} ${match[2]}`;
      return str;
    }
    return upperCaseValue;
  },
  UK: (value: string) => {
    if (!value) return '';
    const postcode = value.replace(/\s+/g, '').toUpperCase();
    if (postcode.length < 5 || postcode.length > 7) return postcode;
    // 拆分成前缀和后缀（后三位始终为后缀）
    const outward = postcode.slice(0, postcode.length - 3);
    const inward = postcode.slice(-3);
    return `${outward} ${inward}`;
  },
};

export const validatePhoneNumber = (phoneNumber: string) => {
  try {
    // 先校验格式
    const phoneFormatRegex =
      PhoneFormatRegexMapping[EcEnv.NEXT_PUBLIC_COUNTRY.toUpperCase() as keyof typeof PhoneFormatRegexMapping];
    if (phoneFormatRegex && !phoneFormatRegex.test(phoneNumber)) {
      return false;
    }
    // 再校验是否为有效手机号
    const phoneUtil = PhoneNumberUtil.getInstance();
    const phoneNumberObject = phoneUtil.parse(phoneNumber, EcEnv.NEXT_PUBLIC_COUNTRY.toUpperCase());
    const res = phoneUtil.isValidNumber(phoneNumberObject);
    return res;
  } catch (error) {
    // console.log('error-isValidNumber---', typeof error);
    return false;
  }
};
/**
 * 手机号格式化 ：
 * - US：+1(xxx)xxx-xxxx；
 * - AU：+61 x xxxx xxxx
 * - SG：+65 xxxx xxxx
 * - CA：+1(xxx)xxx-xxxx
 */
export const phoneNumberFormattingUtils = {
  SG: (value: string) => value,
  AU: (value: string) => value,
  US: (value: string) => {
    if (!value) return '';
    const match = value.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return value;
  },
  CA: (value: string) => {
    if (!value) return '';
    const match = value.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return value;
  },
  UK: (value: string) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    const str = digits.startsWith('44') ? digits.replace('44', '+44 ') : digits;
    const reg = /^(?![+0])/; // 不以+或者0开头时，自动补充+44
    if (reg.test(str)) {
      return `+44 ${str}`;
    }
    return str;
  },
};

export const STATES = {
  US: {
    AL: 'Alabama',
    AK: 'Alaska',
    AS: 'American Samoa',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District Of Columbia',
    FM: 'Federated States Of Micronesia',
    FL: 'Florida',
    GA: 'Georgia',
    GU: 'Guam',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MH: 'Marshall Islands',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    MP: 'Northern Mariana Islands',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PW: 'Palau',
    PA: 'Pennsylvania',
    PR: 'Puerto Rico',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VI: 'Virgin Islands',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
  },
  AU: {
    ACT: 'Australian Capital Territory',
    NSW: 'New South Wales',
    NT: 'Northern Territory',
    QLD: 'Queensland',
    SA: 'South Australia',
    TAS: 'Tasmania',
    VIC: 'Victoria',
    WA: 'Western Australia',
  },
  CA: {
    AB: 'Alberta',
    BC: 'British Columbia',
    MB: 'Manitoba',
    NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador',
    NS: 'Nova Scotia',
    ON: 'Ontario',
    PE: 'Prince Edward Island',
    QC: 'Quebec',
    SK: 'Saskatchewan',
    NT: 'Northwest Territories',
    NU: 'Nunavut',
    YT: 'Yukon',
  },
};

export const getStateOptions = (region: 'AU' | 'US' | 'CA') => {
  const target = STATES[region];
  if (!target) return [];
  const stateOptions = Object.entries(target).map(([key, value]) => ({
    label: value,
    value: key,
  }));
  return stateOptions || [];
};

export const address1DefaultValueReWrite = (defaultAddress: CustomerAddressEntity | CustomerAddressEntity_V2) => {
  if (!defaultAddress) return '';
  let streetNumber = '';
  let street = '';
  if ('streetNumber' in defaultAddress) {
    streetNumber = defaultAddress.streetNumber;
  } else if ('street_number' in defaultAddress) {
    streetNumber = defaultAddress.street_number;
  }
  if ('street' in defaultAddress) {
    street = defaultAddress.street;
  }
  if (!streetNumber && !street) {
    return '';
  }
  return `${streetNumber ?? ''} ${street ?? ''}`.trim();
};
